import { useEffect, useLayoutEffect, useState } from 'react';
import { useAuthStore } from '../store/authStore';
import { TimingScope } from '../types/learningTiming';
import { ForegroundClock, timingId } from '../utils/foregroundClock';
import { deliverTiming, timingQueue } from '../services/learningTimingApi';
// One owner per runtime. Cross-tab/device exclusivity is deliberately unverified;
// the server excludes overlapping runtime segments and labels multi-runtime totals partial.
const owners: {clock: ForegroundClock | null; priority: number}[] = [];
let timeline: {actor: string; id: string; origin: number} | undefined;
let pageSuspended = false;
function updateOwners() {
  const selected = [...owners].sort((a, b) => b.priority - a.priority)[0];
  const foreground = !pageSuspended && document.visibilityState === 'visible' && document.hasFocus();
  // Pause the previous owner BEFORE starting a child; no parent/child overlap.
  owners.filter(o => o !== selected).forEach(o => o.clock?.setActive(false));
  selected?.clock?.setActive(foreground);
}
/** Non-learning overlays (e.g. provider configuration) must pause their parent. */
export function useLearningTimingPause(paused: boolean) {
  useLayoutEffect(() => {
    if (!paused) return;
    const owner = {clock: null, priority: 100};
    owners.push(owner); updateOwners();
    return () => {owners.splice(owners.indexOf(owner), 1); updateOwners();};
  }, [paused]);
}
export function useForegroundTiming(scope: TimingScope | null, ready: boolean,
  contentRevision: string | null = null, unsupported = false, priority = 0) {
  const actor = useAuthStore(s => s.user?.id);
  const student = useAuthStore(s => s.user?.primaryRole === 'student' && !s.user?.isAdmin &&
    !s.user?.roles?.some(r => r.name !== 'student'));
  const token = useAuthStore(s => s.token);
  const [rotation, setRotation] = useState(0);
  const key = JSON.stringify(scope);
  useLayoutEffect(() => {
    if (!actor || !student || !token || !scope || (!ready && !unsupported)) return;
    // A page may have returned from BFCache while no learning surface existed.
    if (!owners.length && document.visibilityState === 'visible' && document.hasFocus()) pageSuspended = false;
    if (!timeline || timeline.actor !== actor || performance.now() - timeline.origin > 72000000)
      timeline = {actor, id: timingId(), origin: performance.now()};
    const currentTimeline = timeline;
    const rotationTimer = window.setTimeout(() => setRotation(r => r + 1), 4 * 60 * 60 * 1000);
    const clock = new ForegroundClock(scope, contentRevision, currentTimeline.id, timingId(),
      () => performance.now() - currentTimeline.origin, p => timingQueue(actor).enqueue(p), unsupported);
    const owner = {clock, priority};
    owners.push(owner);
    updateOwners();
    const visibility = () => {
      clock.checkpoint(document.visibilityState === 'hidden' ? 'hidden' : 'blur');
      updateOwners(); void deliverTiming(actor);
    };
    const blur = () => { clock.setActive(false, 'blur'); void deliverTiming(actor); };
    const pagehide = () => {
      clock.checkpoint('pagehide'); clock.setActive(false, 'pagehide'); clock.markCoverage('pagehide');
      pageSuspended = true; updateOwners();
      // Best effort, not a terminal close: BFCache pages can resume this visit.
      void deliverTiming(actor, true);
    };
    const pageshow = () => { pageSuspended = false; visibility(); };
    const interval = window.setInterval(() => {
      updateOwners(); clock.sample(); void deliverTiming(actor);
    }, 1000);
    const online = () => { void deliverTiming(actor); };
    document.addEventListener('visibilitychange', visibility);
    window.addEventListener('blur', blur);
    window.addEventListener('focus', visibility);
    window.addEventListener('pageshow', pageshow);
    window.addEventListener('pagehide', pagehide);
    window.addEventListener('online', online);
    // A synchronous logout subscription stops capture before another account can own it.
    const unsubscribe = useAuthStore.subscribe(state => {
      if (state.user?.id !== actor || !state.token) {
        clock.close('logout');
        if (timeline?.actor === actor) timeline = undefined;
      }
    });
    void deliverTiming(actor);
    return () => {
      unsubscribe(); window.clearInterval(interval); window.clearTimeout(rotationTimer);
      document.removeEventListener('visibilitychange', visibility);
      window.removeEventListener('blur', blur); window.removeEventListener('focus', visibility);
      window.removeEventListener('pageshow', pageshow); window.removeEventListener('pagehide', pagehide);
      window.removeEventListener('online', online);
      clock.close();
      owners.splice(owners.indexOf(owner), 1); updateOwners(); void deliverTiming(actor);
    };
    // Scope primitives, not object identity or numeric display indexes, rotate visits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actor, student, token, key, ready, contentRevision, unsupported, priority, rotation]);
}
/** Mounted once at the app root: navigation away from learning must not stop retries. */
export function useTimingDelivery() {
  const actor = useAuthStore(s => s.user?.id);
  const token = useAuthStore(s => s.token);
  const student = useAuthStore(s => s.user?.primaryRole === 'student');
  useEffect(() => {
    if (!actor || !token || !student) return;
    const retry = () => { void deliverTiming(actor); };
    const unload = () => { void deliverTiming(actor, true); };
    retry();
    const timer = window.setInterval(() => { void deliverTiming(actor); }, 5000);
    window.addEventListener('online', retry);
    window.addEventListener('pagehide', unload);
    return () => {
      window.clearInterval(timer); window.removeEventListener('online', retry);
      window.removeEventListener('pagehide', unload);
    };
  }, [actor, token, student]);
}
